import { useEffect, useRef } from "react";
import usePaicRedesignPlanStore, { getOtherDraftVersion } from "Store/usePaicRedesignPlanStore";

// Whether leaving the draft's other version needs the user's OK first: it has
// unsaved changes, or it is the saved plan being edited (View Planning's
// Edit). A version that was only browsed can be dropped without asking.
export const needsSwitchConfirm = (otherVersion) =>
  Boolean(otherVersion?.hasUnsavedChanges || otherVersion?.isInSavedPlan);

/**
 * One PAIC plan covers one version, so a version page reached with ANOTHER
 * version in the draft (a pasted link, browser Back/Forward — the versions
 * list asks on row click itself) must either ask before leaving it, or drop
 * it when it was only browsed.
 *
 * The check waits for `isVersionReady` (the opened version loaded, and any
 * plan being loaded by Edit is in): a version that turns out to be
 * unavailable (archived, unpublished, not visible) never asks, so the user
 * can't discard their work for a page that won't open — the draft is left
 * untouched. Until then the page shows its spinner, so nothing can be edited
 * on this version in the meantime.
 *
 * onAsk(otherVersion) is called when the user has to choose; it may change
 * every render (only the latest one is used).
 */
const usePaicVersionSwitchGuard = ({ versionId, isVersionReady, onAsk }) => {
  const onAskRef = useRef(onAsk);
  onAskRef.current = onAsk;

  useEffect(() => {
    if (!versionId || !isVersionReady) return;
    const otherVersion = getOtherDraftVersion(usePaicRedesignPlanStore.getState(), versionId);
    if (!otherVersion) return;
    if (needsSwitchConfirm(otherVersion)) onAskRef.current(otherVersion);
    else usePaicRedesignPlanStore.getState().reset();
  }, [versionId, isVersionReady]);
};

export default usePaicVersionSwitchGuard;
